import is from '@sindresorhus/is';
import { useMemo } from 'react';

import type { CommentLanguageOption } from './types';
import { useCommentLanguageSummaryQuery } from './use-comment-language-summary-query';

export type OtherLanguageWithComments = {
    readonly id: string;
    readonly name: string;
    readonly count: number;
};

type UseOtherLanguagesWithCommentsParams = {
    readonly architectureId: string;
    readonly availableLanguages: readonly CommentLanguageOption[];
    readonly currentLanguage: string;
    readonly itemId: string;
    readonly version: number;
};

export const useOtherLanguagesWithComments = ({
    architectureId,
    availableLanguages,
    currentLanguage,
    itemId,
    version,
}: UseOtherLanguagesWithCommentsParams): readonly OtherLanguageWithComments[] => {
    const { data: counts } = useCommentLanguageSummaryQuery({ architectureId, itemId, version });

    return useMemo(() => {
        if (is.undefined(counts)) {
            return [];
        }

        return availableLanguages.reduce<OtherLanguageWithComments[]>((result, language) => {
            if (language.id === currentLanguage) {
                return result;
            }
            const count = counts.get(language.id) ?? 0;
            if (count > 0) {
                result.push({ count, id: language.id, name: language.name });
            }
            return result;
        }, []);
    }, [availableLanguages, counts, currentLanguage]);
};
